import Filter from "./filter";
import { Currencies } from "@/utils/currencies";
import { request } from "@/store";
import { DEFAULT_EMPTY_ROW_SIZE } from "@/utils/constants";
import { flattenTree } from "@/utils";
import { aggregateTableHeaders } from "@/utils/profit_and_loss_table_fields";
import { getProfitAndLossAggregateTableData } from "@/utils/profit_and_loss_transformers";
const profitAndLoss = {
  state: {
    sellers_filter: new Filter([], [], "id"),
    marketplaces_filter: new Filter([], [], "sales_channel"),
    products: new Filter([], [], "products"),
    currencies: new Filter(Currencies, [], "symbol"),
    segments_filter: new Filter([], [], "segments"),
    brands_filter: new Filter([], [], "brands"),
    filterManager: null,
    aggregateTableGroupBy: {
      key: "aggregate",
      name: "Aggregate"
    },
    aggregateTablePeriod: {
      title: "Period",
      start: {
        date: null,
        format: 'YYYY-MM-DD'
      },
      end: {
        date: null,
        format: 'YYYY-MM-DD'
      },
      key: "monthly",
      range: {
        start: null,
        end: null
      },
      compare: {
        start: null,
        end: null
      }
    },
    aggregateTable: {
      data: {
        items: new Array(DEFAULT_EMPTY_ROW_SIZE),
        minMax: {},
        colGroups: aggregateTableHeaders
      }
    }
  },
  mutations: {
    profitAndLossSellersFilterSet(state, payload) {
      state.sellers_filter.restore(payload);
    },
    profitAndLossMarketplacesFilterSet(state, payload) {
      state.marketplaces_filter.restore(payload);
    },
    profitAndLossDefaultCurrencySet(state, payload) {
      // We need to update the currency filter
      state.currencies.selectOnly({
        symbol: payload
      }, 'symbol');
      state.currencies.apply();
    },
    profitAndLossSetFilterManager(state, payload) {
      state.filterManager = payload;
    },
    profitAndLossSetAggregateTableItems(state, payload) {
      state.aggregateTable.data.items = payload;
    },
    profitAndLossSetAggregateTableMinMax(state, payload) {
      state.aggregateTable.data.minMax = payload;
    }
  },
  getters: {
    profitAndLossGetSellers(state) {
      return () => {
        return state.sellers_filter.filterValues;
      };
    },
    profitAndLossGetMarketplaces(state) {
      return () => {
        return state.marketplaces_filter.filterValues.map(v => v.toLowerCase());
      };
    },
    profitAndLossGetSkus(state) {
      return () => {
        return state.products.selected.map(product => product.sku).filter(Boolean);
      };
    },
    profitAndLossGetAsins(state) {
      return () => {
        return state.products.selected.map(product => product.asin).filter(Boolean);
      };
    },
    profitAndLossGetAggregateGroupBy(state) {
      return () => {
        return state.aggregateTableGroupBy;
      };
    },
    profitAndLossGetAggregatePeriod(state) {
      return () => {
        return state.aggregateTablePeriod;
      };
    },
    profitAndLossGetFilters(state) {
      return () => {
        return {
          seller_id: state.sellers_filter.filterValues,
          sales_channel: state.marketplaces_filter.filterValues,
          asin_list: Filter.merge({
            source1: 'asin_list',
            source2: 'asin'
          }, state.segments_filter.selected, state.products.selected),
          sku_list: Filter.merge({
            source1: 'sku_list',
            source2: 'sku'
          }, state.segments_filter.selected, state.products.selected),
          brand_list: Filter.flatten('brand_list', state.brands_filter.selected)
        };
      };
    }
  },
  actions: {
    profitAndLossSetAggregatePeriod({
      state
    }, period) {
      state.aggregateTablePeriod = period;
    },
    profitAndLossSetDefaultCurrency({
      getters,
      commit
    }) {
      commit('profitAndLossDefaultCurrencySet', getters.currencyConfigQueryGet().currency);
    },
    async profitAndLossGenerateGoogleSheet(_, payload) {
      try {
        let result = await request("/profit_and_loss/generate/googlesheet", "POST", payload);
        if (result) {
          return result;
        }
      } catch (err) {
        console.error(err);
      }
    },
    profitAndLossSetFilterManager({
      commit
    }, payload) {
      commit("profitAndLossSetFilterManager", payload);
    },
    async profitAndLossFetchAggregate({
      commit,
      getters
    }) {
      const params = {
        ...getters.profitAndLossGetFilters(),
        period: getters.profitAndLossGetAggregatePeriod(),
        ...getters.currencyConfigQueryGet()
      };

      // Check if `seller_id` is provided
      if (!('seller_id' in params)) throw new Error("Sellers are required. No key `seller_id` found in the request.");
      if (!params?.seller_id.length) throw new Error("Sellers are required. No value found for `seller_id` in the request.");
      let response = await request("/profit_and_loss/aggregate", "POST", params, false, true, "cors");
      if (!response) {
        return [];
      }
      try {
        response = getProfitAndLossAggregateTableData(response);
        commit('profitAndLossSetAggregateTableItems', response);
        const flattened = response.map(item => flattenTree(item, 'pc_net_revenue_evol')).flat().filter(Boolean);
        const absFlattened = flattened.filter(v => !!v).map(v => Math.abs(v));
        const minMax = {
          evolution_pp: {
            min: Math.min(...absFlattened),
            max: Math.max(...absFlattened)
          }
        };
        commit('profitAndLossSetAggregateTableMinMax', minMax);
      } catch (error) {
        console.error('Error processing profit and loss aggregate response:', error);
        throw error;
      }
    }
  }
};
export default profitAndLoss;